// Today — the phone home screen: my actions due today and overdue · tomorrow's legal agenda · approvals waiting · inbox counts.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Calendar, Check, ChevronRight, ClipboardList, Inbox, Loader2, Megaphone, PiggyBank, RefreshCw, Scale, Bell, MailOpen } from "lucide-react";
import { toast } from "sonner";
import api, { getAllApprovals, updateLegalEvent } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BottomSheet, PullToRefresh, SectionTitle } from "@/components/phone/PhoneBits";
import { cn } from "@/lib/utils";
import { flattenApprovals, ApprovalCard, ApprovalSheet } from "./TodayApprovals";
import { WorldClocks } from "@/components/WorldClocks";

const SOURCE_META = {
  complaint: { label: "Complaint", icon: Megaphone, tint: "text-rose-700 bg-rose-50 border-rose-200" },
  legal: { label: "Legal action", icon: Scale, tint: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  collections: { label: "Collections", icon: PiggyBank, tint: "text-amber-700 bg-amber-50 border-amber-200" },
};
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const shiftIso = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const dueLabel = (iso, today) => {
  if (!iso) return { text: "No date", tone: "text-slate-500" };
  if (iso < today) { const n = Math.round((new Date(today) - new Date(iso)) / 86400000); return { text: `${n}d overdue`, tone: "text-red-700 font-semibold" }; }
  if (iso === today) return { text: "Today", tone: "text-amber-700 font-semibold" };
  return { text: new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }), tone: "text-slate-600" };
};

function ActionRow({ a, today, onOpen, onDone, onSnooze }) {
  const meta = SOURCE_META[a.source] || SOURCE_META.legal;
  const due = dueLabel(a.due_date, today);
  const Icon = meta.icon;
  const title = (a.title || "").split(" — ");
  return (
    <div className={cn("rounded-xl border bg-white px-3 py-2.5 shadow-sm", a.overdue && "border-l-4 border-l-red-500")} data-testid={`phone-action-${a.id}`}>
      <button type="button" onClick={onOpen} className="w-full text-left flex items-start gap-3 sf-tap">
        <span className={cn("w-9 h-9 rounded-lg border flex items-center justify-center shrink-0", meta.tint)}><Icon className="w-4 h-4" /></span>
        <span className="flex-1 min-w-0">
          <span className="block text-[14px] font-semibold leading-tight truncate">{title[0]}</span>
          {title[1] && <span className="block text-[13px] text-slate-700 truncate">{title.slice(1).join(" — ")}</span>}
          <span className="block text-[11px] text-slate-500 truncate">{a.subtitle}</span>
        </span>
        <span className={cn("text-[12px] shrink-0 tabular-nums", due.tone)}>{due.text}</span>
      </button>
      {a.source === "legal" && a.event_id && (
        <div className="mt-2 flex gap-2">
          <Button size="sm" className="h-9 flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={onDone} data-testid={`phone-action-done-${a.event_id}`}><Check className="w-4 h-4 mr-1" /> Done</Button>
          <Button size="sm" variant="outline" className="h-9 flex-1" onClick={onSnooze} data-testid={`phone-action-snooze-${a.event_id}`}><Calendar className="w-4 h-4 mr-1" /> Re-diarise</Button>
        </div>
      )}
    </div>
  );
}

function SnoozeSheet({ action, onClose, onDone }) {
  const { user } = useAuth();
  const [date, setDate] = useState(() => shiftIso(todayIso(), 1));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (reason.trim().length < 10) { toast.error("A reason of at least 10 characters is required to re-diarise"); return; }
    setBusy(true);
    try {
      await updateLegalEvent(action.event_id, { date, rediarise_reason: reason.trim(), actor: user?.name || user?.email || "Ops" });
      toast.success(`Re-diarised to ${new Date(`${date}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  return (
    <BottomSheet open onClose={onClose} title="Re-diarise this action" testid="phone-snooze-sheet"
      footer={<Button className="w-full h-12" onClick={save} disabled={busy} data-testid="phone-snooze-save">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />} Move it</Button>}>
      <div className="text-sm font-medium">{action.title}</div>
      <div className="flex gap-2 flex-wrap">
        {[["Tomorrow", 1], ["+3 days", 3], ["+1 week", 7]].map(([l, n]) => <button key={l} type="button" onClick={() => setDate(shiftIso(todayIso(), n))} className={cn("h-9 px-3 rounded-full border text-[13px] font-medium sf-tap", date === shiftIso(todayIso(), n) ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300")} data-testid={`phone-snooze-quick-${n}`}>{l}</button>)}
      </div>
      <Input type="date" value={date} min={todayIso()} onChange={(e) => setDate(e.target.value)} className="h-12 text-base" data-testid="phone-snooze-date" />
      <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Why is it moving? (e.g. Chaser sent to the court 21/09, awaiting the Order)" className="text-base" data-testid="phone-snooze-reason" />
    </BottomSheet>
  );
}

function Tile({ icon: Icon, label, count, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} className="rounded-xl border bg-white px-3 py-3 text-left shadow-sm active:bg-slate-50 sf-tap flex items-center gap-3" data-testid={testid}>
      <span className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></span>
      <span className="flex-1 min-w-0"><span className="block text-[13px] font-medium truncate">{label}</span><span className="block text-[11px] text-slate-500">{count > 0 ? `${count} waiting` : "Nothing waiting"}</span></span>
      {count > 0 && <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">{count > 99 ? "99+" : count}</span>}
    </button>
  );
}

export default function Today() {
  const navigate = useNavigate();
  const { user, hasPermission, hasAnyPermission } = useAuth();
  const { currentRegion } = useRegion();
  const [actions, setActions] = useState(null);
  const [approvals, setApprovals] = useState(null);
  const [inbox, setInbox] = useState({});
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(null);           // {kind: approval, row} | {kind: snooze, action}
  const today = todayIso();
  const tomorrow = shiftIso(today, 1);
  const canApprove = hasAnyPermission(["can_approve_credit", "page_sales"]);

  const load = useCallback(async () => {
    const dept = (d) => api.get(`/inbox/${d}/count`, { params: { region: currentRegion } });
    const [acts, appr, accounts, legal, apps, postbox] = await Promise.allSettled([
      user?.email ? api.get(`/my-actions?email=${encodeURIComponent(user.email)}`) : Promise.reject(new Error("no email")),
      canApprove ? getAllApprovals((user?.role || "admin").toLowerCase(), currentRegion) : Promise.resolve(null),
      dept("accounts"), dept("legal"),
      api.get("/applications/inbox/count", { params: { region: currentRegion } }),
      api.get("/legal/mail-inbox/count"),
    ]);
    setActions(acts.status === "fulfilled" ? acts.value.data : { actions: [], counts: {} });
    setApprovals(appr.status === "fulfilled" ? appr.value : null);
    setInbox({
      accounts: accounts.status === "fulfilled" ? accounts.value.data?.count || 0 : 0,
      legal: legal.status === "fulfilled" ? legal.value.data?.count || 0 : 0,
      apps: apps.status === "fulfilled" ? apps.value.data?.count || 0 : 0,
      postbox: postbox.status === "fulfilled" ? postbox.value.data?.count || 0 : 0,
    });
    setLoading(false);
  }, [user?.email, user?.role, currentRegion, canApprove]);
  useEffect(() => { load(); }, [load]);

  const all = useMemo(() => actions?.actions || [], [actions]);
  const needsMe = useMemo(() => all.filter((a) => a.source !== "collections" && (a.overdue || a.due_today)), [all]);
  const tomorrowLegal = useMemo(() => all.filter((a) => a.source === "legal" && a.due_date === tomorrow), [all, tomorrow]);
  const collectionsMine = all.filter((a) => a.source === "collections");
  const approvalRows = useMemo(() => flattenApprovals(approvals), [approvals]);
  // Deep link from an "Approval waiting" notification: /?approval=<id>
  const [searchParams, setSearchParams] = useSearchParams();
  const approvalParam = searchParams.get("approval");
  useEffect(() => {
    if (!approvalParam || !approvals) return;
    const row = approvalRows.find((r) => String(r.id) === approvalParam);
    if (row) setSheet({ kind: "approval", row }); else toast.info("That approval has already been dealt with");
    setSearchParams((p) => { p.delete("approval"); return p; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalParam, approvals]);
  const greeting = (() => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; })();
  const firstName = (user?.name || user?.email || "").split(/[ @]/)[0] || "there";
  const ageingPath = currentRegion === "AUS" ? "/aus/collections/ageing" : "/collections/ageing";

  const completeLegal = async (a) => {
    try {
      await updateLegalEvent(a.event_id, { status: "completed", actor: user?.name || user?.email || "Ops" });
      toast.success("Marked done");
      setActions((prev) => prev ? { ...prev, actions: prev.actions.filter((x) => x.id !== a.id) } : prev);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <PullToRefresh onRefresh={load}>
      <div className="space-y-4 pb-6" data-testid="phone-today">
        <header className="px-1 pt-1">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</div>
          <h1 className="text-2xl font-bold text-slate-900 leading-tight" data-testid="phone-today-greeting">{greeting}, {firstName}</h1>
        </header>

        <WorldClocks compact />

        {loading ? <div className="py-12 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Pulling today together…</div> : (
          <>
            <section className="space-y-2" data-testid="phone-today-actions">
              <SectionTitle action={<button type="button" onClick={() => navigate("/my-actions")} className="text-[12px] text-primary inline-flex items-center gap-0.5 h-8 sf-tap" data-testid="phone-today-all-actions">All my actions <ArrowRight className="w-3 h-3" /></button>}>
                Needs you today · {needsMe.length}
              </SectionTitle>
              {needsMe.length === 0 ? (
                <div className="rounded-xl border bg-white px-3 py-4 text-sm text-slate-600 flex items-center gap-2" data-testid="phone-today-actions-empty"><Check className="w-4 h-4 text-emerald-600" /> Nothing overdue or due today — you're clear.</div>
              ) : needsMe.slice(0, 12).map((a) => (
                <ActionRow key={a.id} a={a} today={today} onOpen={() => navigate(a.deep_link)} onDone={() => completeLegal(a)} onSnooze={() => setSheet({ kind: "snooze", action: a })} />
              ))}
              {needsMe.length > 12 && <button type="button" onClick={() => navigate("/my-actions")} className="w-full h-10 text-[13px] text-primary sf-tap">+{needsMe.length - 12} more on My Actions</button>}
              {collectionsMine.length > 0 && hasPermission("can_view_collections") && (
                <button type="button" onClick={() => navigate(ageingPath)} className="w-full rounded-xl border bg-white px-3 py-2.5 shadow-sm text-left flex items-center gap-3 sf-tap active:bg-slate-50" data-testid="phone-today-collections">
                  <span className="w-9 h-9 rounded-lg border text-amber-700 bg-amber-50 border-amber-200 flex items-center justify-center"><PiggyBank className="w-4 h-4" /></span>
                  <span className="flex-1 min-w-0"><span className="block text-[14px] font-semibold">{collectionsMine.length} arrears file{collectionsMine.length === 1 ? "" : "s"} assigned to you</span><span className="block text-[11px] text-slate-500">{collectionsMine.filter((a) => a.overdue).length} past 7 days without a payment · open the worklist</span></span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </button>
              )}
            </section>

            {hasAnyPermission(["can_view_collections", "page_legal"]) && (
              <section className="space-y-2" data-testid="phone-today-legal">
                <SectionTitle action={<button type="button" onClick={() => navigate("/legal/calendar")} className="text-[12px] text-primary inline-flex items-center gap-0.5 h-8 sf-tap" data-testid="phone-today-legal-calendar">Legal calendar <ArrowRight className="w-3 h-3" /></button>}>
                  Legal agenda · tomorrow
                </SectionTitle>
                {tomorrowLegal.length === 0 ? <div className="rounded-xl border bg-white px-3 py-3 text-sm text-slate-500" data-testid="phone-today-legal-empty">Nothing of yours is diarised for tomorrow.</div>
                  : tomorrowLegal.map((a) => <ActionRow key={a.id} a={a} today={today} onOpen={() => navigate(a.deep_link)} onDone={() => completeLegal(a)} onSnooze={() => setSheet({ kind: "snooze", action: a })} />)}
              </section>
            )}

            {canApprove && (
              <section className="space-y-2" data-testid="phone-today-approvals">
                <SectionTitle action={<button type="button" onClick={() => navigate("/approvals")} className="text-[12px] text-primary inline-flex items-center gap-0.5 h-8 sf-tap" data-testid="phone-today-all-approvals">All approvals <ArrowRight className="w-3 h-3" /></button>}>
                  Approvals waiting · {approvalRows.length}
                </SectionTitle>
                {approvalRows.length === 0 ? <div className="rounded-xl border bg-white px-3 py-3 text-sm text-slate-500 flex items-center gap-2" data-testid="phone-today-approvals-empty"><ClipboardList className="w-4 h-4" /> Nothing waiting for your approval.</div>
                  : approvalRows.slice(0, 8).map((r) => <ApprovalCard key={`${r.kind}-${r.id}`} row={r} onOpen={() => setSheet({ kind: "approval", row: r })} />)}
              </section>
            )}

            <section className="space-y-2" data-testid="phone-today-inboxes">
              <SectionTitle>Inboxes</SectionTitle>
              <div className="grid grid-cols-1 gap-2">
                {hasPermission("can_view_collections") && <Tile icon={Inbox} label="Accounts Inbox" count={inbox.accounts} onClick={() => navigate(currentRegion === "AUS" ? "/aus/collections/inbox" : "/collections/inbox")} testid="phone-today-inbox-accounts" />}
                {hasAnyPermission(["page_legal", "legal_head"]) && <Tile icon={Scale} label="Legal Inbox" count={inbox.legal} onClick={() => navigate(currentRegion === "AUS" ? "/aus/legal/inbox" : "/legal/inbox")} testid="phone-today-inbox-legal" />}
                {hasAnyPermission(["can_view_team_cases", "page_applications_inbox"]) && <Tile icon={Bell} label="Application Hub" count={inbox.apps} onClick={() => navigate("/applications/inbox")} testid="phone-today-inbox-apps" />}
                {currentRegion !== "AUS" && hasAnyPermission(["can_view_collections", "page_legal"]) && <Tile icon={MailOpen} label="Postbox" count={inbox.postbox} onClick={() => navigate("/legal/mail-inbox")} testid="phone-today-inbox-postbox" />}
              </div>
            </section>

            <div className="flex justify-center"><button type="button" onClick={load} className="text-[12px] text-slate-500 inline-flex items-center gap-1 h-9 sf-tap" data-testid="phone-today-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button></div>
          </>
        )}

        {sheet?.kind === "approval" && <ApprovalSheet row={sheet.row} onClose={() => setSheet(null)} onDone={load} />}
        {sheet?.kind === "snooze" && <SnoozeSheet action={sheet.action} onClose={() => setSheet(null)} onDone={load} />}
      </div>
    </PullToRefresh>
  );
}
